/**
 * Recovers the SPA from stale dynamic-import chunks after a new deploy.
 *
 * When a new frontend build ships, hashed chunk filenames change. A user whose
 * tab still holds the old index.html requests a chunk that no longer exists the
 * moment they navigate to a lazy route, producing "Failed to fetch dynamically
 * imported module" (Sentry BEETEGRE-V2-1) — every route in App.tsx is lazy, so
 * any of them can trip this, not just the one that happened to surface it.
 *
 * Vite dispatches `vite:preloadError` on the window for exactly this failure.
 * We reload once to pull the fresh index.html + current chunk hashes. A
 * timestamp guard in sessionStorage prevents an infinite reload loop when the
 * chunk is genuinely unavailable (not merely stale): a second failure inside
 * the guard window is left to surface to the ErrorBoundary instead of looping.
 *
 * We deliberately do NOT call `event.preventDefault()`. Cancelling the event
 * makes Vite's `__vitePreload` resolve the dynamic import to `undefined` instead
 * of rejecting; React.lazy then reads `undefined.default` and throws a fresh
 * "Cannot read properties of undefined (reading 'default')" (Sentry
 * BEETEGRE-V2-3). Letting Vite rethrow keeps the import a clean rejection that
 * React handles normally, while location.reload() recovers the user regardless.
 */

const RELOAD_GUARD_KEY = 'beetegre:chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 10_000;

interface ChunkReloadEnv {
  addEventListener: (type: string, listener: (event: Event) => void) => void;
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
  location: Pick<Location, 'reload'>;
}

export const VITE_PRELOAD_ERROR_EVENT = 'vite:preloadError';

let reloadRequestedAt: number | null = null;

/**
 * True for the guard window after the handler has called `location.reload()` for a stale chunk.
 * The failed import still rejects and reaches the ErrorBoundary before the page unloads; error
 * reporting uses this to drop that one expected failure while a second failure (reload did not
 * help) stays reported. Time-boxed because a `beforeunload` prompt the user answers with "Stay"
 * cancels the reload, and a flag that never cleared would hide every later chunk failure.
 */
export function isChunkReloadPending(now: number = Date.now()): boolean {
  return reloadRequestedAt !== null && now - reloadRequestedAt < RELOAD_GUARD_WINDOW_MS;
}

/**
 * True on the boot that follows a stale-chunk reload (the guard timestamp is still inside its
 * window). The failure that caused the reload is dropped from error reporting, so this boot is the
 * only place the recovery can be counted.
 */
export function reloadedForStaleChunk(
  storage: Pick<Storage, 'getItem'> = window.sessionStorage,
  now: number = Date.now(),
): boolean {
  try {
    const at = Number(storage.getItem(RELOAD_GUARD_KEY) ?? 0);
    return at > 0 && now - at < RELOAD_GUARD_WINDOW_MS;
  } catch {
    return false;
  }
}

export function installChunkReloadHandler(env: ChunkReloadEnv = window): void {
  reloadRequestedAt = null;
  env.addEventListener(VITE_PRELOAD_ERROR_EVENT, () => {
    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(env.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    } catch {
      // sessionStorage blocked (private mode / disabled) — attempt one reload anyway.
    }

    // A second preload failure inside the guard window means the reload did not
    // fix it (chunk truly gone). Let this one surface instead of reloading again.
    if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) return;

    try {
      env.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // ignore storage failures — the reload is still worth attempting.
    }
    reloadRequestedAt = Date.now();
    env.location.reload();
  });
}
